// Admin › AI Prompts: the system-wide library of prompts J2 Tools sends to AI
// models (ai_prompts / ai_prompt_versions) and the models they can use
// (ai_models). Code finds a prompt by its handle (prompt key, module.name) and
// runs its Active version. The Modules tab lists every prompt, one row each;
// Add and Edit open the Prompt Editor for that one prompt. The "AI Prompts"
// tool lets an employee view; its admin access lets them edit.

// REACT IMPORTS
import React, { useContext, useEffect, useState } from "react";

// MSAL
import { useMsal } from "@azure/msal-react";

// MUI COMPONENTS
import { CircularProgress } from "@mui/material";

// CUSTOM COMPONENTS
import ModulesTab from "./ModulesTab";
import ModelsTab from "./ModelsTab";
import PromptEditor from "./PromptEditor";

// CONTEXT, API, CONSTANTS
import { UserContext } from "../../context/UserContext";
import { getAPICall, postAPICall } from "../../config/apiCalls";

// CSS
import "../ITBudget/ITBudget.css";

const BASE = "/api/ai-prompts";

const AIPrompts = () => {
	const { instance, accounts } = useMsal();
	const employeeID = useContext(UserContext);
	const [access, setAccess] = useState(null);
	const [tab, setTab] = useState("modules");
	const [handlers, setHandlers] = useState([]);
	const [editor, setEditor] = useState(null); // { key } for a prompt, { key: null } for a new one

	useEffect(() => {
		postAPICall(instance, accounts[0], "/api/logs/access/add", {
			page_name: "Admin - AI Prompts",
			employeeID: employeeID,
			notes: "Accessed the AI Prompts page",
		}).catch(() => {});
		getAPICall(instance, accounts[0], `${BASE}/me`)
			.then((res) => setAccess(res.data))
			.catch(() => setAccess({ view: false, edit: false }));
		getAPICall(instance, accounts[0], `${BASE}/test-handlers`)
			.then((res) => setHandlers(res.data))
			.catch(() => {});
	}, []);

	if (!access) {
		return (
			<div className="itBudgetEmpty">
				<CircularProgress />
			</div>
		);
	}
	if (!access.view) return <div className="itBudgetEmpty">You don't have access to AI Prompts.</div>;

	return (
		<div className="itBudgetPage">
			<div className="itBudgetHeader">
				<div>
					<div className="itBudgetBreadcrumb">
						Admin / AI Prompts{editor ? ` / ${editor.key || "New module prompt"}` : ""}
					</div>
					<div className="itBudgetTitle">{editor ? "Prompt Editor" : "AI Prompts"}</div>
					<div className="itBudgetSubtitle">
						{editor
							? "One prompt: its details, and its versions — edit a Draft, test it, then activate it."
							: "Every prompt J2 Tools sends to an AI model, versioned. Code runs each prompt's Active version."}
						{access.edit ? "" : " View only."}
					</div>
				</div>
			</div>

			{editor ? (
				<PromptEditor
					promptKey={editor.key}
					canEdit={access.edit}
					handlers={handlers}
					onBack={() => setEditor(null)}
					onCreated={(key) => setEditor({ key })}
				/>
			) : (
				<>
					<div className="itBudgetTabs">
						{[
							{ id: "modules", label: "Modules" },
							{ id: "models", label: "Models" },
						].map((t) => (
							<button key={t.id} className={`itBudgetTab${tab === t.id ? " itBudgetTab--active" : ""}`} onClick={() => setTab(t.id)}>
								{t.label}
							</button>
						))}
					</div>
					{tab === "modules" && <ModulesTab canEdit={access.edit} onOpen={(key) => setEditor({ key })} />}
					{tab === "models" && <ModelsTab canEdit={access.edit} />}
				</>
			)}
		</div>
	);
};

export default AIPrompts;
